import styles from "../../Pages/UserManagement/UserManagement.module.scss";
import LicenseCard from "../LicenseCard/LicenseCard";
import { Card, Empty, Skeleton } from "antd";
import { TbLayoutGridAdd } from "react-icons/tb";
import colors from "../../Utils/colors";
import Meta from "antd/es/card/Meta";
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLicenseTypes } from "../../Redux/User/userSlice";
import { RootState } from "../../Redux/store";
import AssignTrialModal from "../Trial/AssignTrialModal";
import UserManagementFunction from "../../Functions/UserManagement/UserManagement";
import ErrorModal from "../Modals/ErrorModal";
import { LicenseSettingsProvider } from "../LicenseSettings/LicenseSettingsContext";
import { License, StoredCustomerLicenses } from "../../Utils/types";
import { CustomerLicense, UserLicense } from "../../Utils/types";
import { isAiUsersLicense } from "../LicenseSettings/utils/licenseType";

interface LicenseCardProps {}

const LicenseGrid: React.FC<LicenseCardProps> = () => {
  const accessToken = useSelector(
    (state: RootState) => state.access.accessToken
  );

  const sessionToken = useSelector((state: RootState) => state.user.token);
  const userData = useSelector((state: RootState) => state.user);
  const { custID } = userData.user!;
  const [isTrialVisible, setIsTrialVisible] = useState(false);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [errorVisible, setErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(false);
  const dispatch = useDispatch();

  function closeModal() {
    setIsTrialVisible(false);
  }

  const handleCloseError = () => {
    setErrorVisible(false);
  };

  const notifyError = (message: string) => {
    setErrorMessage(message);
    setErrorVisible(true);
  };

  const fetchData = async () => {
    if (!accessToken || !sessionToken || !custID) {
      return;
    }

    try {
      const response = await UserManagementFunction.getAllLicencesByCustId(
        accessToken,
        sessionToken,
        { custId: custID.toString() }
      );
      if (response.status) {
        const newLicenses: License[] = [...response.data.licenses];
        setLicenses(newLicenses);
        const userCustomerLicenses: StoredCustomerLicenses[] =
          response.data.licenses.flatMap((license: License) =>
            license.customerLicenses.map(
              (customerLicense: CustomerLicense) => ({
                key: customerLicense.id,
                licenseId: license.id,
                type: customerLicense.type,
                value: customerLicense.value,
                usedValue: customerLicense.usedValue,
                description: license.description,
                userLicenses: customerLicense.userLicenses.map(
                  (userLicense: UserLicense) => ({
                    ...userLicense,
                    customerLicenseId: customerLicense.id,
                    licenseDescription: license.description,
                  })
                ),
              })
            )
          );
        dispatch(setLicenseTypes(userCustomerLicenses));
      }
    } catch (error) {
      console.log("Error fetching data: ", error);
      notifyError("Error fetching data: " + error);
    }
  };
  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [JSON.stringify(custID)]);

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [updateTrigger]);

  const handleLicenseDelete = useCallback(() => {
    setUpdateTrigger((prev) => !prev);
  }, []);

  const handleForceUpdate = useCallback(() => {
    setUpdateTrigger((prev) => !prev);
  }, []);

  const hasCustomerLicenses = licenses.some(
    (licence) => licence.customerLicenses.length > 0
  );

  return (
    <>
      <div className={styles.licenceContainer}>
        {loading ? (
          <>
            <div className={styles.licenceCardWrapper}>
              <Skeleton.Node
                active={true}
                style={{ width: "400px", height: "300px" }}
              >
                <div></div>
              </Skeleton.Node>
            </div>
            <div className={styles.licenceCardWrapper}>
              <Skeleton.Node
                active={true}
                style={{ width: "400px", height: "300px" }}
              >
                <div></div>
              </Skeleton.Node>
            </div>
            <div className={styles.licenceCardWrapper}>
              <Skeleton.Node
                active={true}
                style={{ width: "400px", height: "300px" }}
              >
                <div></div>
              </Skeleton.Node>
            </div>
          </>
        ) : (
          <>
            {hasCustomerLicenses
              ? licenses.map((licence) => {
                  const aiUsersLicense =
                    isAiUsersLicense(licence.description) ||
                    isAiUsersLicense(licence.name);

                    return (
                      <React.Fragment key={licence.id}>
                      {licence.customerLicenses.map((customerLicense) => {
                        if (aiUsersLicense && !customerLicense.isActive) {
                          return null;
                        }

                        return (
                          <div
                            key={customerLicense.id}
                            className={styles.licenceCardWrapper}
                          >
                            <LicenseSettingsProvider
                              usedValue={Number(customerLicense?.usedValue) ?? 0}
                              isTrial={customerLicense.type === "TRIAL"}
                              fetchLicenses={handleForceUpdate}
                            >
                              <LicenseCard
                                key={customerLicense.id}
                                license={{
                                  ...customerLicense,
                                }}
                                type={licence.description}
                                onLicenseDelete={handleLicenseDelete}
                              />
                            </LicenseSettingsProvider>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              : userData?.user?.loginType !== "super" && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "75vh",
                      width: "100%",
                    }}
                  >
                    <Empty description={"You have no assigned licenses."} />
                  </div>
                )}
          </>
        )}
        {!loading && userData?.user?.loginType === "super" && (
          <Card
            className={styles.addTrialCardStyle}
            onClick={() => setIsTrialVisible(true)}
          >
            <div className={styles.addNewServiceImg}>
              <TbLayoutGridAdd
                className={styles.addNewServiceImgImage}
                size={50}
                color={colors.colorBorderGray}
              />
              <Meta
                title="Add new service"
                description="Create and configure a new service"
              />
            </div>
          </Card>
        )}
      </div>
      <AssignTrialModal
        visible={isTrialVisible}
        onCancel={closeModal}
        onTrialAssigned={() => setUpdateTrigger((prev) => !prev)}
      />
      <ErrorModal
        visible={errorVisible}
        errorMessage={errorMessage}
        onClose={handleCloseError}
      />
    </>
  );
};

export default LicenseGrid;
